import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as apiUtil from "../../../util/apiUtil";
import { aesDecrypt } from "../../../util/aes256";

type Status = "loading" | "success" | "error";

export default function NaverOAuthCallbackPage() {
  const { appUid } = useParams<{ appUid: string }>();
  const [status, setStatus] = useState<Status>("loading");
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    // URLSearchParams는 + 를 공백으로 디코딩하므로 복원 필요
    const rawState = params.get("state");
    const stateParam = rawState ? rawState.replace(/ /g, "+") : null;

    if (!code || !stateParam || !appUid) {
      setStatus("error");
      return;
    }

    let cmpnId: string | null = null;
    try {
      const decrypted = aesDecrypt(stateParam);
      const stateObj = JSON.parse(decrypted);
      cmpnId = stateObj.cmpnId;
    } catch {
      setStatus("error");
      return;
    }

    if (!cmpnId) {
      setStatus("error");
      return;
    }

    // 1. WAS OAuth 콜백 처리
    const redirectUri = `${window.location.origin}/naver/${appUid}/callback`;

    apiUtil.asyncRequest({
      url: `/api/bizchat/naver-app/${appUid}/oauth/callback`,
      method: apiUtil.requestMethod.POST,
      data: { code, cmpnId, redirectUri },
      success: (result: any) => {
        const isNewMember: boolean = result?.newMember ?? false;
        const redirectUrl: string = result?.redirectUrl ?? "";

        // 2. COLLECT_COMPLETE 퍼널 로그
        const sessionId = window.sessionStorage.getItem("naverLoginSessionId");
        apiUtil.asyncRequest({
          url: "/api/common/naver/funnel",
          method: apiUtil.requestMethod.POST,
          data: { cmpnId, eventType: "COLLECT_COMPLETE", isNewMember, sessionId },
        });

        // 3. 고객사 redirect URL이 있으면 이동
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }

        setStatus("success");
      },
      fail: () => setStatus("error"),
    });
  }, [appUid]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {status === "loading" && (
          <>
            <div style={styles.spinner} />
            <p style={styles.message}>처리 중입니다...</p>
          </>
        )}
        {status === "success" && (
          <>
            <div style={styles.iconSuccess}>✓</div>
            <h2 style={styles.title}>동의가 완료되었습니다</h2>
            <p style={styles.desc}>감사합니다. 창을 닫으셔도 됩니다.</p>
          </>
        )}
        {status === "error" && (
          <>
            <div style={styles.iconError}>!</div>
            <h2 style={styles.title}>처리에 실패했습니다</h2>
            <p style={styles.desc}>다시 시도해주세요.</p>
          </>
        )}
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "48px 40px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    width: "320px",
    textAlign: "center",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #e0e0e0",
    borderTopColor: "#03C75A",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  message: {
    color: "#888",
    fontSize: "15px",
    margin: 0,
  },
  iconSuccess: {
    width: "56px",
    height: "56px",
    backgroundColor: "#03C75A",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "28px",
    fontWeight: "bold",
  },
  iconError: {
    width: "56px",
    height: "56px",
    backgroundColor: "#ff4d4f",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "28px",
    fontWeight: "bold",
  },
  title: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#1a1a1a",
    margin: 0,
  },
  desc: {
    fontSize: "14px",
    color: "#666",
    margin: 0,
  },
};
